import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  ImagePlus,
  Wand2,
  ListChecks,
  X,
  ArrowRight,
  ArrowLeft,
  Check,
  Lightbulb,
} from "lucide-react";
import { aiAssist, type AiAssistResult, type AiStep } from "./reportApi";
import { useTranslation } from "../../i18n";

export function AiAssistBar({
  onApply,
  onWalkthrough,
}: {
  onApply: (result: AiAssistResult) => void;
  onWalkthrough: (steps: AiStep[]) => void;
}) {
  const { t } = useTranslation();
  const EXAMPLES = [
    t("reportBuilder.example1", "Scan counts by hospital for the last month, with a bar chart"),
    t("reportBuilder.example2", "Daily patrol activity with peak hours and active devices"),
    t("reportBuilder.example3", "Checkpoint coverage: scanned vs unscanned by hospital"),
  ];
  const [prompt, setPrompt] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AiAssistResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function pickImage(file: File | null) {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setError(t("reportBuilder.imageTooLarge", "That image is a bit large — please use one under 6 MB."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function run() {
    if (!prompt.trim() && !image) {
      setError(t("reportBuilder.describeOrSketch", "Describe the report you want, or attach a sketch."));
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await aiAssist(prompt.trim(), image ?? undefined);
      if (!res.report.widgets.length) {
        setError(t("reportBuilder.noLayout", "I couldn't turn that into a layout — try describing the metrics and how to group them."));
      } else {
        setResult(res);
      }
    } catch (e) {
      setError((e as Error).message || t("reportBuilder.assistantUnavailable", "The assistant is unavailable right now."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="no-print mb-4 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-white">
          <Sparkles size={16} />
        </span>
        <h2 className="text-sm font-bold text-gray-800">{t("reportBuilder.aiAssistant", "AI report assistant")}</h2>
        <span className="rounded-md bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
          {t("reportBuilder.beta", "Beta")}
        </span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <div className="flex flex-1 items-start gap-2 rounded-lg border border-gray-300 bg-white p-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run();
            }}
            rows={2}
            placeholder={t("reportBuilder.aiPlaceholder", "Describe the report you want — e.g. “scan counts by hospital for the last month with a bar chart.” Or sketch a layout on paper and upload a photo.")}
            className="min-h-[2.5rem] w-full resize-none border-0 text-sm text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-0"
          />
          {image && (
            <div className="relative shrink-0">
              <img src={image} alt={t("reportBuilder.sketchAlt", "sketch")} className="h-14 w-14 rounded-md border border-gray-200 object-cover" />
              <button
                onClick={() => setImage(null)}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-gray-800 p-0.5 text-white"
                title={t("reportBuilder.removeImage", "Remove image")}
              >
                <X size={11} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-stretch gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            title={t("reportBuilder.uploadSketchTitle", "Upload a photo of a hand-drawn report layout")}
          >
            <ImagePlus size={16} /> <span className="hidden sm:inline">{t("reportBuilder.sketch", "Sketch")}</span>
          </button>
          <button
            onClick={run}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Sparkles size={16} /> {busy ? t("reportBuilder.thinking", "Thinking…") : t("reportBuilder.generateBtn", "Generate")}
          </button>
        </div>
      </div>

      {!result && !error && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-400">{t("reportBuilder.try", "Try:")}</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              onClick={() => setPrompt(ex)}
              className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 hover:border-blue-300 hover:text-blue-700"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {result && (
        <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{result.report.title}</span> — {result.message}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            {(result.report.widgets.length === 1
              ? t("reportBuilder.widgetCountOne", "{n} widget")
              : t("reportBuilder.widgetCountMany", "{n} widgets")
            ).replace("{n}", String(result.report.widgets.length))}
            {!result.available && t("reportBuilder.draftedOffline", " · drafted offline")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                onApply(result);
                setResult(null);
                setPrompt("");
                setImage(null);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Wand2 size={15} /> {t("reportBuilder.buildItForMe", "Build it for me")}
            </button>
            <button
              onClick={() => onWalkthrough(result.steps)}
              disabled={!result.steps.length}
              className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              <ListChecks size={15} /> {t("reportBuilder.walkMeThrough", "Walk me through it")}
            </button>
            <button
              onClick={() => setResult(null)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-800"
            >
              {t("reportBuilder.dismiss", "Dismiss")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Walkthrough — a friendly, numbered tutorial panel pinned to the corner.
// ---------------------------------------------------------------------------
const TARGET_LABEL: Record<string, { key: string; en: string }> = {
  ai_bar: { key: "reportBuilder.targetAiBar", en: "AI assistant bar" },
  templates: { key: "reportBuilder.targetTemplates", en: "Templates button" },
  toolbar: { key: "reportBuilder.targetToolbar", en: "widget toolbar" },
  data_fields: { key: "reportBuilder.targetDataFields", en: "Data fields panel" },
  canvas: { key: "reportBuilder.targetCanvas", en: "report canvas" },
  config_panel: { key: "reportBuilder.targetConfigPanel", en: "configure panel" },
  export: { key: "reportBuilder.targetExport", en: "Export button" },
  preview: { key: "reportBuilder.targetPreview", en: "Preview toggle" },
  save: { key: "reportBuilder.targetSave", en: "Save button" },
};

export function Walkthrough({
  steps,
  onHighlight,
  onClose,
}: {
  steps: AiStep[];
  onHighlight: (target: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const step = steps[i];

  // Highlight the current step's region whenever the step changes (incl. mount).
  useEffect(() => {
    onHighlight(steps[i]?.target ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  function go(next: number) {
    setI(Math.max(0, Math.min(steps.length - 1, next)));
  }

  function finish() {
    onHighlight(null);
    onClose();
  }

  if (!steps.length) return null;

  return (
    <div className="no-print fixed bottom-4 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-blue-200 bg-white shadow-2xl">
      <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-700 to-blue-600 px-4 py-2.5 text-white">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-amber-300" />
          <span className="text-sm font-bold">{t("reportBuilder.buildTogether", "Let's build it together")}</span>
        </div>
        <button onClick={finish} className="rounded p-1 hover:bg-white/15" title={t("common.close", "Close")}>
          <X size={16} />
        </button>
      </div>

      <div className="px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
            {i + 1}
          </span>
          <span className="text-sm font-semibold text-gray-900">{step.title}</span>
        </div>
        <p className="text-sm leading-relaxed text-gray-700">{step.detail}</p>
        {step.target && TARGET_LABEL[step.target] && (
          <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
            <ArrowRight size={12} /> {t("reportBuilder.lookAt", "Look at the {target}").replace("{target}", t(TARGET_LABEL[step.target].key, TARGET_LABEL[step.target].en))}
          </p>
        )}

        {/* progress dots */}
        <div className="mt-3 flex items-center gap-1">
          {steps.map((_, idx) => (
            <span
              key={idx}
              className={`h-1.5 flex-1 rounded-sm ${idx <= i ? "bg-blue-500" : "bg-gray-200"}`}
            />
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={() => go(i - 1)}
            disabled={i === 0}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 disabled:opacity-40"
          >
            <ArrowLeft size={14} /> {t("common.back", "Back")}
          </button>
          <span className="text-xs text-gray-400">
            {t("reportBuilder.stepOf", "Step {n} of {total}").replace("{n}", String(i + 1)).replace("{total}", String(steps.length))}
          </span>
          {i < steps.length - 1 ? (
            <button
              onClick={() => go(i + 1)}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {t("reportBuilder.next", "Next")} <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={finish}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              <Check size={14} /> {t("reportBuilder.done", "Done")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
